import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Cortex Modal (DESIGN_LANGUAGE_SPEC §19). Rendered via a portal to document.body at
// z-[99999]; backdrop blur; closes on Escape AND backdrop click (content clicks do not
// close); scale+fade entrance with a 200ms isClosing exit before unmount; max-h with
// internal body scroll; footer stacks on mobile (flex-col sm:flex-row). Focus moves into
// the modal on open, is trapped within it while open (Tab/Shift+Tab wrap — WCAG 2.1.2 /
// 2.4.3), and is restored to the previously focused element on close.

const EXIT_DURATION_MS = 200;

// Tab order within the modal. Mirrors RawKeyRevealModal's trap so Tab cannot escape the
// dialog to the page behind the backdrop.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | undefined;
  /** Optional icon shown in the gradient header container. */
  icon?: ReactNode | undefined;
  children?: ReactNode | undefined;
  footer?: ReactNode | undefined;
  /**
   * Card width (2026-08-21). `md` is the historical max-w-md; `wide` is 50% wider
   * (max-w-2xl = 42rem vs 28rem) for editors with tabular content — e.g. the provider
   * form's model-alias rows, whose dropdowns need the horizontal room.
   */
  size?: 'md' | 'wide' | undefined;
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isClosing, setIsClosing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Focus trap: keep Tab/Shift+Tab inside the dialog so focus can't escape behind the backdrop.
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const root = cardRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    window.setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, EXIT_DURATION_MS);
  }, [onClose]);

  // Escape closes the modal while open.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Move focus into the modal on open; restore it on close.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    cardRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center p-4 transition-all duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      data-testid="modal-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        data-testid="modal-backdrop"
        onClick={handleClose}
      />

      {/* Modal card */}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`relative flex max-h-[85vh] w-full ${
          size === 'wide' ? 'max-w-2xl' : 'max-w-md'
        } flex-col overflow-hidden rounded-2xl shadow-2xl outline-none transition-all duration-200 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        } ${
          isDark
            ? 'border border-white/[0.1] bg-gradient-to-br from-[#1a1a2e] to-[#12121a] shadow-black/50'
            : 'border border-slate-200 bg-white shadow-slate-200/50'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-0">
          <div className="flex items-center gap-4">
            {icon && (
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/20 to-purple-600/10 text-indigo-400">
                {icon}
              </div>
            )}
            <div>
              <h3
                id={titleId}
                className={`text-xl font-semibold ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                {title}
              </h3>
              {subtitle && (
                <p
                  className={`mt-0.5 text-sm ${
                    isDark ? 'text-white/40' : 'text-slate-500'
                  }`}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className={`rounded-xl p-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
              isDark
                ? 'text-white/40 hover:bg-white/[0.06] hover:text-white'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            }`}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Body — scrolls internally when content overflows. */}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer — stacks on mobile, row on larger screens. */}
        {footer && (
          <div className="flex flex-col gap-3 p-6 pt-0 sm:flex-row">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
